import { usePollingInterval } from 'src/hooks/usePollingInterval'
import type { Theme } from '@material-ui/core'
import { CardContent, makeStyles } from '@material-ui/core'
import type { Activity, SearchActivitiesQuery } from '@paintscout/api'
import { useSearchActivitiesQuery } from '@paintscout/api'
import type { ActivityTimelineProps } from '@paintscout/ui'
import {
  ActivityTimeline,
  Button,
  Typography,
  useDialogs,
  useIsFirstRender,
  ActivityActions,
  ActivityAction
} from '@paintscout/ui'
import ActivityLink from 'src/components/common/ActivityLink'
import React, { useCallback, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import EyeIcon from '@material-ui/icons/Visibility'

export interface DashboardActivityProps {
  query: string
  onUpdate?: () => void
  showContent?: boolean
  showClear?: boolean
  ActivityComponent?: ActivityTimelineProps['ActivityComponent']
  noResultsMessage?: string
  onDataReceived?: (data: SearchActivitiesQuery) => any
  onClear?: () => void
  title?: string
  newTheme?: boolean
  onAction?: (action: string, activity: Activity) => any
  actions?: boolean
  disabled?: boolean
  skipSanitize?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  root: () => {
    return {
      position: 'relative'
    }
  },
  noActivityMessage: {
    height: 160, // match size of estimates table with no results
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'pre-wrap'
  },
  clearButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(3),
    zIndex: 1
  },
  title: {
    marginBottom: theme.spacing(2)
  }
}))

export default function DashboardActivity({
  query,
  onUpdate,
  showContent,
  showClear,
  ActivityComponent = ActivityLink,
  noResultsMessage = 'No activity found.',
  onClear,
  onDataReceived,
  title,
  onAction,
  actions = false,
  disabled = false,
  skipSanitize = false
}: DashboardActivityProps) {
  const classes = useStyles({})
  const { stack } = useDialogs()
  const pollingInterval = usePollingInterval(120000)
  const isFirstRender = useIsFirstRender()
  const { loading, data, fetchMore, updateQuery } = useSearchActivitiesQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: stack.length > 0 ? null : pollingInterval,
    notifyOnNetworkStatusChange: false,
    variables: {
      query,
      limit: 10,
      skipSanitize
    },
    onCompleted: useCallback((data) => {
      onUpdate?.()
      onDataReceived?.(data)
    }, []),
    onError: useCallback((error) => {
      Sentry.captureException(error)
    }, [])
  })

  // bust cache if query changes after first query has loaded
  useEffect(() => {
    if (!isFirstRender) {
      updateQuery((prev) => {
        return {
          ...prev,
          searchActivities: null
        }
      })
    }
  }, [query])

  const rows = data?.searchActivities?.activities ?? []
  const totalRows = data?.searchActivities?.total_rows || 0
  const hasMore = rows.length >= 0 && rows.length < totalRows

  const showClearButton = !loading && showClear && !!onClear

  return (
    <div className={classes.root}>
      <CardContent>
        <>
          {title && (
            <Typography variant="h2" className={classes.title}>
              {title}
            </Typography>
          )}
          {showClearButton && (
            <div className={classes.clearButton}>
              <ActivityActions>
                <ActivityAction onClick={onClear} icon={EyeIcon}>
                  Mark all as read
                </ActivityAction>
              </ActivityActions>
            </div>
          )}
          {!loading && rows.length === 0 && (
            <Typography variant="subtitle1" align="center" className={classes.noActivityMessage}>
              {noResultsMessage}
            </Typography>
          )}

          <ActivityTimeline
            loading={loading && !data}
            activities={rows}
            showDocTypeDetails
            showContent={showContent}
            ActivityComponent={ActivityComponent}
            actions={actions}
            onAction={onAction}
            disabled={disabled}
          />
          {hasMore && (
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                fetchMore({
                  variables: {
                    query,
                    bookmark: data.searchActivities.bookmark
                  },
                  updateQuery(prev, { fetchMoreResult }) {
                    if (!fetchMoreResult) {
                      return prev
                    }

                    return {
                      ...prev,
                      ...fetchMoreResult,
                      searchActivities: {
                        ...prev.searchActivities,
                        ...fetchMoreResult.searchActivities,
                        activities: [
                          ...prev.searchActivities.activities,
                          ...fetchMoreResult.searchActivities.activities
                        ]
                      }
                    }
                  }
                })
              }}
              disabled={loading}
            >
              View More
            </Button>
          )}
        </>
      </CardContent>
    </div>
  )
}
